/**
 * useForgeLibrary (incident 4b — 2026-06-10).
 *
 * Loads the customer's main-Forge library (setups -> bows/sights, saved
 * builds -> arrows) from the parent storefront's first-party mirror and
 * exposes it READ-ONLY for display on My Bows / My Arrows.
 *
 * Source: the `sparrowForge.kvRead` bridge (see ./parentStorage). The main
 * Forge mirrors `sparrow_setups_v2` + `sparrow_arrow_builds` to the parent
 * page; this hook reads them back. If the hosting section has no kvRead
 * handler yet (forge-tool-embed.liquid patch not published), the read times
 * out and this returns an empty library — the page renders exactly as before.
 *
 * Read-only by design (guardrail): we never write these into local profile
 * storage. The UI offers an explicit, user-initiated "Save to My Bows/Arrows"
 * copy instead.
 */
import { useEffect, useState } from 'react';
import {
  requestParentValues,
  FORGE_SETUPS_KEY,
  FORGE_SAVED_BUILDS_KEY,
} from './parentStorage';
import { collectForgeLibrary, type ForgeLibrary } from './forgeLibraryImport';

export type ForgeLibraryStatus = 'loading' | 'empty' | 'ready' | 'unavailable';

export interface UseForgeLibrary extends ForgeLibrary {
  status: ForgeLibraryStatus;
}

export function useForgeLibrary(): UseForgeLibrary {
  const [lib, setLib] = useState<ForgeLibrary>({ bows: [], arrows: [] });
  const [status, setStatus] = useState<ForgeLibraryStatus>('loading');

  useEffect(() => {
    let cancelled = false;
    requestParentValues([FORGE_SETUPS_KEY, FORGE_SAVED_BUILDS_KEY])
      .then((vals) => {
        if (cancelled) return;
        const collected = collectForgeLibrary(
          vals[FORGE_SETUPS_KEY],
          vals[FORGE_SAVED_BUILDS_KEY],
        );
        setLib(collected);
        const hasAny = collected.bows.length > 0 || collected.arrows.length > 0;
        const answered = FORGE_SETUPS_KEY in vals || FORGE_SAVED_BUILDS_KEY in vals;
        // No keys in the reply at all => no bridge (old/unpatched section).
        setStatus(hasAny ? 'ready' : answered ? 'empty' : 'unavailable');
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { ...lib, status };
}
